import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { logError, logEvent } from "@src/appV2/lib/analytics";
import { USER_EVENTS } from "@src/constants";
import { usCountyCode } from "@src/constants/phone";
import { getCognitoLocalStorageState } from "@src/lib/cognito/src/lib/cognitoLocalStorageInspector";
import { getCognitoSingleton } from "@src/lib/cognito/src/lib/cognitoSingleton";
import { enqueueSnackbar } from "notistack";
import { Dispatch } from "redux";

import {
  getReferralRate,
  requestAgentOTP,
  updateAgentLoggedInTime,
  validateAgentOTPv2,
} from "./api";
import { onLoggedOut } from "./init";
import { ActionType, SessionAction } from "./session.model";
import { GetState } from "../store.model";

function showErrorMessage(message: string) {
  enqueueSnackbar({
    variant: "error",
    message,
  });
}
/**
 * @description this function call the v3/requestOTP endpoint and doesn't expect any sign in token in return
 */
export const reVerifyPhoneRequestOTP =
  (phone: string) =>
  async (dispatch: Dispatch, getState: GetState): Promise<SessionAction | boolean> => {
    const { isSignup } = getState().session;

    const res = await requestAgentOTP(phone, isSignup);
    if (!isSignup) {
      if (!res || res?.error === "AgentNotFound") {
        dispatch({ type: ActionType.SET_IS_VERIFYING_DONE });
        showErrorMessage("No account found. New? Sign up today to join.");
        return false;
      }
    }
    if (res?.error) {
      logError(APP_V2_APP_EVENTS.VALIDATE_OTP_FAILURE, {
        error: res.error,
        metadata: {
          hcpMobile: `${usCountyCode}${phone}`,
        },
      });
      dispatch({ type: ActionType.SET_IS_VERIFYING_DONE });
      showErrorMessage("Unable to verify phone number. Please try again.");
      return false;
    }
    return dispatch({
      type: ActionType.VERIFICATION_ID_RECEIVED,
      data: {
        verification: { phone },
      },
    });
  };

/**
 * Only used for home/signupReVerify?phoneNumber=6086086088
 */
export const signUpReverifyWithOtp =
  (code: string, phone: string) =>
  async (dispatch: Dispatch, getState: GetState): Promise<SessionAction | boolean> => {
    dispatch({
      type: ActionType.VERIFICATION_REQUESTED,
    });
    const { isSignup } = getState().session;
    const res = await validateAgentOTPv2({ phone, code, isSignup });
    if (res?.error) {
      logError(APP_V2_APP_EVENTS.VALIDATE_OTP_FAILURE, {
        error: res.error,
        metadata: {
          hcpMobile: `${usCountyCode}${phone}`,
        },
      });
      dispatch({ type: ActionType.SET_IS_VERIFYING_DONE });
      logEvent(USER_EVENTS.TWO_FACTOR_CODE_DENIED);
      return false;
    }

    // Cognito session is established via the auth listener.
    // The firebaseToken from the backend is no longer used for sign-in.
    logEvent(USER_EVENTS.TWO_FACTOR_CODE_ACCEPTED);
    if (!isSignup) {
      void updateAgentLoggedInTime();
    }
    logEvent(USER_EVENTS.SIGNED_IN);
    return true;
  };

export const loginWithLink =
  (link: string, email: string) =>
  async (dispatch: Dispatch): Promise<SessionAction | boolean> => {
    dispatch({
      type: ActionType.VERIFICATION_REQUESTED,
    });

    try {
      const cognitoAuth = getCognitoSingleton();
      await cognitoAuth.signInWithEmailLink(link, email);
      // FIXME: Automatically disabled during ESLint config update
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      dispatch({ type: ActionType.SET_IS_VERIFYING_DONE });
      showErrorMessage("Error occurred while signing in, please try again.");
      return false;
    }

    void updateAgentLoggedInTime();
    logEvent(USER_EVENTS.SIGNED_IN);
    return true;
  };

export const logout =
  (callback?: () => void) =>
  async (dispatch: Dispatch<any>): Promise<void> => {
    try {
      logEvent(APP_V2_APP_EVENTS.LEGACY_LOGOUT_FUNCTION_STARTED);
      dispatch({
        type: ActionType.LOGOUT_REQUESTED,
      });

      logEvent(USER_EVENTS.LOGGED_OUT);

      try {
        logEvent(APP_V2_APP_EVENTS.COGNITO_SIGN_OUT_INVOKED, {
          source: "legacyLogout",
          ...getCognitoLocalStorageState(),
        });
        await getCognitoSingleton().signOut();
      } catch (cognitoError) {
        logEvent(APP_V2_APP_EVENTS.LEGACY_LOGOUT_FUNCTION_ERROR, {
          error: cognitoError,
          metadata: { source: "cognito-signout" },
        });
      }

      logEvent(APP_V2_APP_EVENTS.LEGACY_LOGOUT_FUNCTION_ENDED);
      callback?.();
    } catch (error) {
      logEvent(APP_V2_APP_EVENTS.LEGACY_LOGOUT_FUNCTION_ERROR, { error });
      throw error;
    }
  };

export const logOutAgentAuth =
  () =>
  async (dispatch: Dispatch): Promise<void> => {
    onLoggedOut(dispatch);
  };

export const reloadAgentProfile =
  (shouldLogError = true) =>
  async (): Promise<void> => {
    try {
      // Force refresh the Cognito token to get updated user attributes
      const cognitoAuth = getCognitoSingleton();
      await cognitoAuth.getIdToken(true);
    } catch (error) {
      if (shouldLogError) {
        const err = error as Error;
        logEvent(USER_EVENTS.ONBOARDING_ERROR, {
          message: "Unable to re-load agent profile",
          error: err?.message,
        });
      }
    }
  };

/**
 * @deprecated
 * Do not remove this until we have decoupled authentication from our legacy Redux implementation
 */
export const setIsSignup = (dispatch: Dispatch): void => {
  localStorage.setItem("isSignup", "true");
  dispatch({
    type: ActionType.SET_IS_SIGNUP,
  });
};

/**
 * @deprecated Review this usage.
 */
export const setReferralRate =
  () =>
  async (dispatch: Dispatch): Promise<void> => {
    const referralRate = await getReferralRate();
    dispatch({
      type: ActionType.UPDATE_REFERRAL_RATE,
      data: { referralRate },
    });
  };
